/**
 * Component: BrowseLink
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R20, BROWSE-V1-R21
 *
 * BROWSE-V1-R20: Reusable cross-module link component rendered on
 *                result cards in Galen Search, Chat, Semantic Search,
 *                and Simple Search. Gated by the `browseLinks` feature
 *                flag (sourced from CONFIG-V1 browse_links_enabled
 *                via BROWSE-V1-R18). Renders nothing when the flag
 *                is false.
 * BROWSE-V1-R21: Opens the /browse viewer in a new browser tab and
 *                passes both guidelineId and sectionId from the
 *                result payload. The target guideline + section is
 *                encoded as `/browse/guidelines/{guidelineId}?sectionId={sectionId}`;
 *                the existing ViewerPage R22 scroll-to-section hook
 *                consumes the query param.
 *
 * Risk Links: R7, R11
 * FMEA Links: FM7, FM11
 */

import React, { useCallback } from 'react';
import { BookOpen } from 'lucide-react';
import { useIsFeatureEnabled } from '@/contexts/FeatureFlagContext';
import { trackUsageAction } from '@/lib/usage-tracking';

export interface BrowseLinkProps {
  /** Guideline identifier from the result payload. Required. */
  guidelineId: number | string;
  /**
   * Section identifier from the result payload. Optional — when
   * omitted the viewer opens at the top of the guideline. BROWSE-V1-R22
   * scrolls to the section when present.
   */
  sectionId?: number | string | null;
  /**
   * LOGGING-V1-R07: Upstream search/chat surface identifier used for
   * COUNTER referrer attribution. Surfaces: 'galen_search',
   * 'semantic_search', 'simple_search', 'chat'.
   */
  searchMode?: string;
  /**
   * Optional document identifier for COUNTER usage tracking. Kept
   * optional so chat citations (which do not carry document_id)
   * still work.
   */
  documentId?: string | null;
  /**
   * Optional ranking position (1-based) on the current result page,
   * used for COUNTER usage tracking on search cards.
   */
  rankingPosition?: number | null;
  /**
   * Optional guideline name for COUNTER usage tracking.
   */
  guidelineName?: string | null;
  /**
   * Optional section name for COUNTER usage tracking.
   */
  sectionName?: string | null;
}

/**
 * BrowseLink renders a single cross-module link that opens the
 * BROWSE-V1 viewer for the cited guideline in a new browser tab.
 *
 * BROWSE-V1-R20: Returns null when the `browseLinks` feature flag
 * is off so deployments that disable the flag see no link at all.
 *
 * BROWSE-V1-R21: The anchor uses `target="_blank"` +
 * `rel="noopener noreferrer"` and embeds both `guidelineId` (path
 * segment) and `sectionId` (query param) from the result payload.
 *
 * FRONTEND-WEB-V1-R10 / LOGGING-V1-R07: Click fires
 * trackUsageAction('link_open', ...) with the same payload shape
 * used by the existing "View Full Result" and "View Full Guideline"
 * buttons so COUNTER attribution stays consistent across all three
 * card actions.
 */
export function BrowseLink({
  guidelineId,
  sectionId,
  searchMode,
  documentId,
  rankingPosition,
  guidelineName,
  sectionName,
}: BrowseLinkProps): React.ReactElement | null {
  // BROWSE-V1-R20: render nothing when the deployment-time flag
  // is off. The flag reads flags.browseLinks which sources from
  // backend enable_browse_links which mirrors the
  // browse_links_enabled Pydantic Setting (R18). R19 guarantees
  // that if this flag is on, the /browse viewer is also mounted.
  const enabled = useIsFeatureEnabled('browseLinks');

  const handleClick = useCallback((): void => {
    // FRONTEND-WEB-V1-R10 / LOGGING-V1-R07: Same usage-tracking
    // payload shape as the existing card actions, so COUNTER
    // attribution for Browse-link clicks matches the other two
    // card-footer actions without creating a new action type.
    void trackUsageAction('link_open', {
      ...(documentId ? { document_id: documentId } : {}),
      ...(rankingPosition != null ? { ranking_position: rankingPosition } : {}),
      ...(searchMode ? { referrer_mode: searchMode } : {}),
      ...(sectionId != null ? { section_id: Number(sectionId) } : {}),
      ...(guidelineName ? { guideline_name: guidelineName } : {}),
      ...(sectionName ? { section_name: sectionName } : {}),
      ...(guidelineId != null ? { guideline_id: String(guidelineId) } : {}),
    });
  }, [
    documentId,
    rankingPosition,
    searchMode,
    sectionId,
    guidelineName,
    sectionName,
    guidelineId,
  ]);

  if (!enabled) {
    return null;
  }

  // BROWSE-V1-R21: target="_blank" opens the viewer in a new tab;
  // rel="noopener noreferrer" is the required safety pair for any
  // user-opened external navigation. Path carries guidelineId;
  // sectionId is passed as the ?sectionId= query param consumed by
  // the existing ViewerPage R22 scroll-to-section hook.
  const href =
    sectionId != null
      ? `/browse/guidelines/${guidelineId}?sectionId=${sectionId}`
      : `/browse/guidelines/${guidelineId}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="inline-flex items-center gap-1 text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
      aria-label={
        sectionName
          ? `Open "${sectionName}" in the Browse viewer (new tab)`
          : 'Open in the Browse viewer (new tab)'
      }
      data-testid="browse-link"
    >
      <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
      View in Browse
    </a>
  );
}

export default BrowseLink;
